import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Icon, type IconName } from '../../primitives';
import { resizableAsideHandleVariants } from './variants';

export interface ResizableAsideProps {
  /** Main content rendered on the left (flex-1). */
  children: ReactNode;
  /** Aside content rendered on the right. */
  aside: ReactNode;
  /** Initial width of the aside in px (default 320). */
  initialWidth?: number;
  /** Minimum width of the aside in px (default 240). */
  minWidth?: number;
  /** Maximum width of the aside in px (default 640). */
  maxWidth?: number;
  /**
   * Width (px) the main column is never squeezed below (default 400). Once the
   * aside can no longer keep `minWidth` alongside it, the layout stacks the
   * aside underneath the main column instead of the two overlapping.
   */
  minContentWidth?: number;
  /**
   * localStorage key for persisting the chosen width AND collapsed state
   * across sessions. The key is used as-is for the width; the `:collapsed`
   * suffix is appended for the collapsed flag.
   */
  storageKey?: string;
  /** Override the gap between main content and the divider (Tailwind class). */
  gapClassName?: string;
  /**
   * Drag threshold (px below `minWidth`) at which the panel snaps closed.
   * Default 40 — i.e. dragging the divider 40px past the min width collapses.
   */
  collapseThreshold?: number;
  /** Icon shown on the collapsed drawer strip (default `notebook-tabs`). */
  collapsedIcon?: IconName;
  /** Accessible label for the handle/strip when collapsed (default 'Expand panel'). */
  expandLabel?: string;
  /** Accessible label for the handle while resizing (default 'Resize panel'). */
  resizeLabel?: string;
}

const COLLAPSED_STRIP_SIZE = 32;
const DEFAULT_COLLAPSE_THRESHOLD = 40;
const DEFAULT_MIN_CONTENT_WIDTH = 400;
// Matches `w-2` on the divider — reserved so the mode decision is taken on the
// space the two columns actually get to share.
const HANDLE_SIZE = 8;

const clampWidth = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const readPersistedWidth = (
  key: string | undefined,
  bounds: { min: number; max: number; fallback: number }
): number => {
  const { min, max, fallback } = bounds;
  if (!key || typeof window === 'undefined') {
    return fallback;
  }
  try {
    const saved = window.localStorage.getItem(key);
    if (!saved) {
      return fallback;
    }
    const parsed = parseInt(saved, 10);
    if (Number.isNaN(parsed)) {
      return fallback;
    }

    return clampWidth(parsed, min, max);
  } catch {
    return fallback;
  }
};

const readPersistedCollapsed = (key: string | undefined): boolean => {
  if (!key || typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(`${key}:collapsed`) === '1';
  } catch {
    return false;
  }
};

const collapsedStripClasses = cn(
  'bg-neutral-muted border border-solid border-neutral-border rounded-md text-neutral-muted-foreground cursor-pointer',
  'transition-colors duration-150',
  'hover:bg-secondary-minimal hover:text-foreground hover:border-secondary',
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-secondary'
);

/**
 * Two-column layout (main content + aside) with a draggable vertical handle
 * between them. Dragging far enough past `minWidth` snaps the aside closed
 * into a thin drawer strip — clicking the strip (or the handle) re-opens it
 * at its last-known width. Width and collapsed state are persisted to
 * localStorage when `storageKey` is supplied.
 *
 * The layout responds to the width of its own container, not the viewport, so
 * it reflows for anything that narrows the page (a side drawer, a split view)
 * and not just for a smaller window: the aside shrinks towards `minWidth`
 * first, then stacks underneath the main column.
 */
export const ResizableAside: FC<ResizableAsideProps> = ({
  children,
  aside,
  initialWidth = 320,
  minWidth = 240,
  maxWidth = 640,
  minContentWidth = DEFAULT_MIN_CONTENT_WIDTH,
  storageKey,
  gapClassName = 'gap-3',
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
  collapsedIcon = 'notebook-tabs',
  expandLabel = 'Expand panel',
  resizeLabel = 'Resize panel',
}) => {
  const [width, setWidth] = useState<number>(() =>
    readPersistedWidth(storageKey, {
      min: minWidth,
      max: maxWidth,
      fallback: initialWidth,
    })
  );
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readPersistedCollapsed(storageKey)
  );
  // Space the two columns share, once the divider and the flex gaps are taken
  // out. `null` until the first measurement, so the first paint keeps the
  // caller's requested width rather than flashing a clamped one.
  const [sharedWidth, setSharedWidth] = useState<number | null>(null);

  const stacked = sharedWidth !== null && sharedWidth - minContentWidth < minWidth;
  const availableAsideWidth =
    sharedWidth === null
      ? maxWidth
      : clampWidth(sharedWidth - minContentWidth, minWidth, maxWidth);
  const renderedWidth = clampWidth(width, minWidth, availableAsideWidth);

  const widthRef = useRef(width);
  widthRef.current = width;
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const availableAsideWidthRef = useRef(availableAsideWidth);
  availableAsideWidthRef.current = availableAsideWidth;

  const draggingRef = useRef(false);
  const didDragRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => {
      const gap = parseFloat(window.getComputedStyle(el).columnGap) || 0;
      setSharedWidth(el.clientWidth - gap * 2 - HANDLE_SIZE);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const persistWidth = useCallback(
    (value: number) => {
      if (!storageKey || typeof window === 'undefined') {
        return;
      }
      try {
        window.localStorage.setItem(storageKey, String(Math.round(value)));
      } catch {
        // ignore — localStorage may be unavailable (private mode, quota)
      }
    },
    [storageKey]
  );

  const persistCollapsed = useCallback(
    (value: boolean) => {
      if (!storageKey || typeof window === 'undefined') {
        return;
      }
      try {
        window.localStorage.setItem(
          `${storageKey}:collapsed`,
          value ? '1' : '0'
        );
      } catch {
        // ignore
      }
    },
    [storageKey]
  );

  const expandToWidth = useCallback(
    (next: number) => {
      const clamped = clampWidth(next, minWidth, maxWidth);
      setWidth(clamped);
      setCollapsed(false);
      persistWidth(clamped);
      persistCollapsed(false);
    },
    [maxWidth, minWidth, persistCollapsed, persistWidth]
  );

  const collapse = useCallback(() => {
    setCollapsed(true);
    persistCollapsed(true);
  }, [persistCollapsed]);

  const handleHandleClick = useCallback(() => {
    // mousedown -> drag -> mouseup raises a synthetic click; swallow it so
    // the click only toggles when the user did not actually drag.
    if (didDragRef.current) {
      didDragRef.current = false;

      return;
    }
    if (collapsedRef.current) {
      expandToWidth(widthRef.current);
    } else {
      collapse();
    }
  }, [collapse, expandToWidth]);

  const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    draggingRef.current = true;
    didDragRef.current = false;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current || !containerRef.current) {
        return;
      }
      const containerRight = containerRef.current.getBoundingClientRect().right;
      const desired = containerRight - event.clientX;

      if (Math.abs(desired - widthRef.current) > 2) {
        didDragRef.current = true;
      }

      if (desired < minWidth - collapseThreshold) {
        if (!collapsedRef.current) {
          setCollapsed(true);
          collapsedRef.current = true;
        }

        return;
      }

      const clamped = clampWidth(
        desired,
        minWidth,
        availableAsideWidthRef.current
      );
      if (collapsedRef.current) {
        setCollapsed(false);
        collapsedRef.current = false;
      }
      setWidth(clamped);
      // Keep the ref in lockstep with state so the synchronous mouseup that
      // can fire on the same tick persists the final value, not the prior one.
      widthRef.current = clamped;
    };

    const onMouseUp = () => {
      if (!draggingRef.current) {
        return;
      }
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      persistWidth(widthRef.current);
      persistCollapsed(collapsedRef.current);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // If we unmount mid-drag (e.g. route change), the cursor/user-select
      // overrides applied on mousedown would otherwise leak globally.
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [collapseThreshold, minWidth, persistCollapsed, persistWidth]);

  const onHandleDoubleClick = useCallback(() => {
    expandToWidth(initialWidth);
  }, [expandToWidth, initialWidth]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (collapsed) {
          expandToWidth(widthRef.current);
        } else {
          collapse();
        }
      }
    },
    [collapse, collapsed, expandToWidth]
  );

  const onStripClick = useCallback(() => {
    expandToWidth(widthRef.current);
  }, [expandToWidth]);

  return (
    <div
      ref={containerRef}
      className={cn('flex', stacked && 'flex-col', gapClassName)}
      data-slot={'resizable-aside'}
      data-testid={'resizable-aside'}
      data-layout={stacked ? 'stacked' : 'columns'}
    >
      <div className={cn('flex-1')} style={{ minWidth: stacked ? 0 : minContentWidth }}>
        {children}
      </div>
      <div
        role={'separator'}
        aria-orientation={stacked ? 'horizontal' : 'vertical'}
        aria-label={collapsed ? expandLabel : resizeLabel}
        aria-valuenow={collapsed ? 0 : Math.round(renderedWidth)}
        aria-valuemin={0}
        aria-valuemax={Math.round(availableAsideWidth)}
        tabIndex={0}
        onMouseDown={stacked ? undefined : onMouseDown}
        onClick={handleHandleClick}
        onDoubleClick={onHandleDoubleClick}
        onKeyDown={onKeyDown}
        className={cn(
          resizableAsideHandleVariants({
            orientation: stacked ? 'horizontal' : 'vertical',
          }),
          collapsed && 'cursor-pointer'
        )}
        data-testid={'resizable-aside-handle'}
      />
      {collapsed ? (
        <button
          type={'button'}
          onClick={onStripClick}
          aria-label={expandLabel}
          className={cn(
            collapsedStripClasses,
            'shrink-0 flex items-center justify-center',
            stacked ? 'w-full' : 'self-stretch'
          )}
          style={
            stacked
              ? { height: `${COLLAPSED_STRIP_SIZE}px` }
              : { width: `${COLLAPSED_STRIP_SIZE}px` }
          }
          data-testid={'resizable-aside-collapsed-strip'}
        >
          <Icon name={collapsedIcon} size={'md'} aria-hidden={true} />
        </button>
      ) : (
        <div
          className={cn(
            'shrink-0 flex flex-col gap-4 [:empty]:hidden',
            stacked && 'w-full'
          )}
          style={stacked ? undefined : { width: `${renderedWidth}px` }}
          data-testid={'resizable-aside-content'}
        >
          {aside}
        </div>
      )}
    </div>
  );
};

export default ResizableAside;
